import { useEffect, useState } from "react";
import styled from "styled-components";

import {
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Grid,
  Typography,
} from "@mui/material";

import CloseIcon from "@mui/icons-material/Close";

import Dialog from "src/components/core/Dialog";
import Button from "src/components/core/Button";
import TextField from "src/components/core/TextField";
import CustomMap from "src/components/ui/Maps/CustomMaps";

import font from "src/styles/font";
import { Black, Red } from "src/styles/color";
import { useSnackbar } from "notistack";
import AddressAutocomplete from "src/components/core/AddressAutocomplete";
import { SpanSubHeader } from "src/components/order/components";
import { checkNotEmpty } from "src/common";
import { updateAddressOrder } from "src/api/order";

// Styled Components
const WrapperTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: ${font.size.lg};
  font-weight: 600;
  color: ${Black};

  span {
    font-size: ${font.size.md};
    font-weight: 500;
    color: ${Red};
  }
`;

const WrapperMapsContainer = styled.div`
  width: 100%;
  padding-top: calc(100% / (16 / 9));
  position: relative;
  margin-top: 16px;
`;

const MapsContainer = styled.div`
  position: absolute;
  border: 0;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  border-radius: 16px;
`;

const FormSection = styled.div`
  margin-bottom: 24px;
`;

const SectionTitle = styled(Typography)`
  font-size: ${font.size.md};
  font-weight: 600;
  color: ${Black};
  margin-bottom: 16px;
`;


const EditAddresDialog = ({ onClose, onSuccess, setIsLoading, orderDetail }) => {
  const { enqueueSnackbar } = useSnackbar();
  
  const [orderForm, setOrderForm] = useState({});

  useEffect(() => {
    if (!orderDetail) return;
    setOrderForm({...orderDetail});

  }, [orderDetail]);

  const handleOnClose = () => {
    if (onClose) onClose();
  };

  const handleMapLocationChange = (coordinates) => {
    setOrderForm(prev => ({
      ...prev,
      shippingAddress: {
        ...prev.shippingAddress,
        recipients: {
          ...prev.shippingAddress.recipients,
          coordinates: {
            lat: coordinates.lat,
            lng: coordinates.lng
          },
        },
      },
    }));
  };

  const handleChangeShippingAddress = (field, value) => {
    setOrderForm(prev => ({
      ...prev,
      shippingAddress: {
        ...prev.shippingAddress,
        [field]: value,
      },
    }));
  };

  const fetchAddress = () => {
    try {
      setIsLoading(true)
      updateAddressOrder({poId: orderDetail._id, order: orderForm})
      .then(() => {
        enqueueSnackbar("บันทึกข้อมูลสำเร็จ", { variant: "success" });
        if (onSuccess) onSuccess();
        handleOnClose();
      });
    } catch (error) {
      console.error("Error updating address:", error);
    } finally {
      setIsLoading(false);
    }

  }

  const isValidForm = orderForm?.shippingAddress?.name &&
    orderForm?.user?.email &&
    orderForm?.shippingAddress?.tel &&
    orderForm?.shippingAddress?.address &&
    orderForm?.shippingAddress?.province &&
    orderForm?.shippingAddress?.district &&
    orderForm?.shippingAddress?.subdistrict &&
    orderForm?.shippingAddress?.zipcode &&
    orderForm?.shippingAddress?.recipients?.coordinates.lat &&
    orderForm?.shippingAddress?.recipients?.coordinates.lng;

  return (
    <Dialog
      open={true}
      onClose={handleOnClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 4,
          minHeight: "600px",
        },
      }}
    >
      <DialogTitle sx={{ p: 3 }}>
        <WrapperTitle>แก้ไขข้อมูลที่อยู่</WrapperTitle>

        <IconButton
          aria-label="close"
          onClick={handleOnClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

        <DialogContent dividers sx={{ py: 2 }}>
          <Grid container spacing={3}>
            {/* ข้อมูลลูกค้า */}
            <Grid item xs={12}>
              <FormSection>
                {/* <SectionTitle variant="h6">
                  ข้อมูลลูกค้า
                </SectionTitle> */}
                
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <SpanSubHeader>ชื่อ *</SpanSubHeader>

                    <TextField
                      fullWidth
                      placeholder="กรุณากรอกชื่อ *"
                      value={orderForm?.shippingAddress?.name}
                      onChange={(e) => handleChangeShippingAddress("name", e.target.value) }
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <SpanSubHeader>เบอร์โทรศัพท์ *</SpanSubHeader>

                    <TextField
                      fullWidth
                      placeholder="0xx-xxx-xxxx *"
                      value={orderForm?.shippingAddress?.tel}
                      onChange={(e) => handleChangeShippingAddress("tel", e.target.value) }
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <SpanSubHeader>อีเมล *</SpanSubHeader>

                    <TextField
                      fullWidth
                      type="email"
                      placeholder="example@email.com *"
                      value={orderForm?.user?.email}
                      disabled
                      onChange={(e) =>   
                        setOrderForm(prev => ({
                          ...prev,
                          user: {
                            ...prev.user,
                            email: e.target.value,
                          },
                        }))
                      }
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <SpanSubHeader>บ้านเลขที่/หมู่บ้าน/ตึก/ซอย *</SpanSubHeader>
                  
                    <TextField
                      fullWidth
                      value={orderForm?.shippingAddress?.address}
                      placeholder="กรุณากรอกที่อยู่ *"
                      onChange={(e) => handleChangeShippingAddress("address", e.target.value) }
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <SpanSubHeader>ตำบล/อำเภอ/จังหวัด/รหัสไปรษณีย์ *</SpanSubHeader>

                    <AddressAutocomplete
                      value={
                        checkNotEmpty(orderForm?.shippingAddress?.province)
                          ? {
                              province:
                              orderForm?.shippingAddress?.province || "",
                              district:
                              orderForm?.shippingAddress?.district || "",
                              subdistrict:
                              orderForm?.shippingAddress?.subdistrict || "",
                              zipcode:
                              orderForm?.shippingAddress?.zipcode || "",
                          }
                          : null
                        }
                      onChange={(value) => {
                        if (value) {
                            setOrderForm((form) => ({
                            ...form,
                            shippingAddress: {
                                ...form.shippingAddress,
                                province: value.province,
                                district: value.district,
                                subdistrict: value.subdistrict,
                                zipcode: value.zipcode,
                            },
                            }));
                        } else {
                            setOrderForm((form) => ({
                            ...form,
                            shippingAddress: {
                                ...form.shippingAddress,
                                province: "",
                                district: "",
                                subdistrict: "",
                                zipcode: "",
                            },
                            }));
                        }
                      }}
                      renderInput={(params) => (
                      <TextField
                          className="TextField-without-border-radius"
                          {...params}
                          variant="outlined"
                          fullWidth
                          required
                      />
                      )}
                    />
                  </Grid>
                </Grid>
              </FormSection>
            </Grid>

            {/* แผนที่ */}
            <Grid item xs={12}>
              <FormSection>
                <SectionTitle variant="h6">
                  เลือกตำแหน่งบนแผนที่ *
                </SectionTitle>
                
                <WrapperMapsContainer>
                  <MapsContainer>
                    <CustomMap
                      markerLocation={{
                        lat: orderForm?.shippingAddress?.recipients?.coordinates.lat,
                        lng: orderForm?.shippingAddress?.recipients?.coordinates.lng,
                      }}
                      changeMarkerLocation={handleMapLocationChange}
                      inputPlaceholder="ค้นหาสถานที่..."
                    />
                  </MapsContainer>
                </WrapperMapsContainer>
                
                <Typography 
                  variant="caption" 
                  color="textSecondary" 
                  sx={{ mt: 1, display: "block" }}
                >
                  คลิกบนแผนที่เพื่อเลือกตำแหน่ง หรือค้นหาสถานที่ในช่องค้นหา
                </Typography>
              </FormSection>
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ p: 3, gap: 2 }}>
          <Button 
            variant="outlined" 
            onClick={handleOnClose}
            sx={{ minWidth: 100 }}
          >
            ยกเลิก
          </Button>
          
          <Button 
            type="submit"
            variant="contained"
            sx={{ minWidth: 100 }}
            disabled={!isValidForm}
            onClick={() => fetchAddress()}
          >
            บันทึก
          </Button>
        </DialogActions>
    </Dialog>
  );
};

export default EditAddresDialog;